import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Where a forced-session-end signal originated. Each forced-logout path logs
 * exactly one breadcrumb so we can reconstruct the sequence after the fact.
 *
 * - `next-api-call-401` — the {@link sessionExpiryInterceptor} saw a 401 on a
 *   gated, non-`/api/auth/*` call (default OnNextApiCall mode).
 * - `force-logout-push` — the `UserHub` `ForceLogout` SignalR push fired
 *   (InstantPush mode). Only genuine displacement sends this; a *stale* cookie
 *   never gets a push, so the ABSENCE of this line near a 401 line is itself the
 *   "stale vs superseded" tell the backend asked us to surface.
 * - `force-logout` — `AuthService.forceLogout` actually ran (or short-circuited
 *   on the burst-collapse latch); carries the navigation outcome.
 */
export type SessionEndSource =
  | 'next-api-call-401'
  | 'force-logout-push'
  | 'force-logout';

/**
 * One structured breadcrumb for a forced-session-end event. Field set is the
 * minimum the backend needs to join FE ⟷ BE on the next occurrence (AS-569
 * handoff §3): a UTC timestamp (added on write), the request that 401'd
 * (`method` + `path`), the `route` the user was on, the `X-Auth-Reason`, what
 * the FE did next (`action` / `navigated` / `landedUrl`), and — for a SignalR
 * push — `pushArrived`. Intentionally NON-sensitive: no tokens, no PII.
 */
export interface SessionEndDiagnosticEvent {
  source: SessionEndSource;
  /** What the FE did in response (e.g. 'force-logout', 'gate-skipped'). */
  action: string;
  /** Route/URL the user was on when the signal fired. */
  route: string;
  method?: string;
  /** Path of the request that 401'd (so it lines up with the BE log `Path`). */
  path?: string;
  /** Raw `X-Auth-Reason` header value (or body `message`) when present. */
  authReason?: string | null;
  /** The {@link ForcedLogoutReason} passed to `forceLogout`, when relevant. */
  reason?: string;
  /** Whether `router.navigate(['/auth/login'])` actually succeeded. */
  navigated?: boolean;
  /** The URL the app actually landed on after the forced logout. */
  landedUrl?: string;
  /** True when a `ForceLogout` SignalR push drove this teardown. */
  pushArrived?: boolean;
}

/**
 * Emits a single console breadcrumb per forced-session-end event so support can
 * correlate a "stuck / signed-out" report against the backend's `ApplicationLog`
 * `Verdict` row.
 *
 * Gated by `!environment.production` — matching the rest of the app's diagnostic
 * logging convention, the breadcrumb prints in dev/staging/UAT only and is
 * silent in production. The event is rare (at most once per session teardown)
 * and the payload carries no secrets — only the join keys the backend listed.
 * Kept dependency-free (console only) so it can be injected from the
 * interceptor, `AuthService`, and the SignalR push handler without any risk of a
 * DI cycle.
 */
@Injectable({ providedIn: 'root' })
export class SessionEndDiagnosticsService {
  record(event: SessionEndDiagnosticEvent): void {
    if (environment.production) {
      return;
    }
    try {
      // eslint-disable-next-line no-console
      console.warn('[session-end]', { at: new Date().toISOString(), ...event });
    } catch {
      // Diagnostics must never break a teardown. Swallow any console failure.
    }
  }
}
